// frontend/src/pages/Encuesta/EncuestaResultadosPage.js
import React, { useCallback, useEffect, useState } from 'react';
import encuestaService from '../../services/encuestaService';
import {
    COLOR_PRIMARIO,
    COLOR_SECUNDARIO,
    NO_APLICA,
    buscarPregunta,
    enunciadoDe,
    etiquetaDeOpcion,
} from './definicionEncuesta';

/**
 * Resultados de la encuesta de satisfacción (/encuesta/resultados). Sólo PROGRAMADOR.
 *
 * Los textos de las preguntas y de las opciones salen de definicionEncuesta.js, el mismo archivo que
 * dibuja el formulario público: el backend devuelve códigos ("p1", "MAS_FACIL") justamente para que
 * la redacción no quede duplicada y no puedan terminar diciendo cosas distintas.
 */
const EncuestaResultadosPage = () => {
    const [resultados, setResultados] = useState(null);
    const [version, setVersion] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);
    const [descargando, setDescargando] = useState(false);

    const cargar = useCallback(async (versionPedida) => {
        setCargando(true);
        setError(null);
        try {
            const datos = await encuestaService.obtenerResultados(versionPedida);
            setResultados(datos);
            setVersion(datos.version);
        } catch (e) {
            setError(e?.response?.data?.message || 'No pudimos cargar los resultados de la encuesta.');
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => {
        cargar(null);
    }, [cargar]);

    const descargarCsv = async () => {
        setDescargando(true);
        try {
            const blob = await encuestaService.descargarCsv(version);
            const url = window.URL.createObjectURL(blob);
            const enlace = document.createElement('a');
            enlace.href = url;
            enlace.download = `encuesta-satisfaccion-${version}.csv`;
            document.body.appendChild(enlace);
            enlace.click();
            document.body.removeChild(enlace);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setError('No pudimos generar el CSV.');
        } finally {
            setDescargando(false);
        }
    };

    if (cargando) {
        return <div style={estilos.aviso}>Cargando resultados...</div>;
    }

    if (error) {
        return <div style={{ ...estilos.aviso, color: '#b91c1c' }}>{error}</div>;
    }

    const versiones = resultados?.versionesDisponibles || [];

    return (
        <div style={estilos.pagina}>
            <div style={estilos.encabezado}>
                <div style={estilos.bloqueTitulo}>
                    <div style={estilos.iconoTitulo}>
                        <i className="bi bi-clipboard2-check" />
                    </div>
                    <div>
                        <h1 style={estilos.titulo}>Encuesta de satisfacción</h1>
                        <p style={estilos.subtitulo}>
                            Respuestas anónimas del personal sobre el sistema.
                        </p>
                    </div>
                </div>
                <div style={estilos.acciones}>
                    {/* El selector aparece recién cuando hay más de una tanda: con una sola no hay
                        nada para elegir y sería un control que confunde más de lo que ayuda. */}
                    {versiones.length > 1 && (
                        <select
                            value={version || ''}
                            onChange={(e) => cargar(e.target.value)}
                            style={estilos.selector}
                        >
                            {versiones.map((v) => (
                                <option key={v} value={v}>Encuesta {v}</option>
                            ))}
                        </select>
                    )}
                    <button
                        type="button"
                        onClick={descargarCsv}
                        disabled={descargando || resultados.totalRespuestas === 0}
                        style={estilos.botonCsv(descargando || resultados.totalRespuestas === 0)}
                    >
                        <i className="bi bi-download" style={{ marginRight: '0.45rem' }} />
                        {descargando ? 'Generando...' : 'Exportar CSV'}
                    </button>
                </div>
            </div>

            <div style={estilos.tarjetaTotal}>
                <div style={estilos.numeroTotal}>{resultados.totalRespuestas}</div>
                <div style={estilos.etiquetaTotal}>
                    {resultados.totalRespuestas === 1 ? 'respuesta recibida' : 'respuestas recibidas'}
                </div>
            </div>

            {resultados.totalRespuestas === 0 ? (
                <div style={estilos.vacio}>
                    Todavía no hay respuestas en esta tanda. El link para responder es <code>/encuesta</code>.
                </div>
            ) : (
                <>
                    <h2 style={estilos.tituloSeccion}>Promedios</h2>
                    <div style={estilos.grillaPromedios}>
                        {resultados.promedios.map((promedio) => (
                            <TarjetaPromedio key={promedio.pregunta} promedio={promedio} />
                        ))}
                    </div>

                    <h2 style={estilos.tituloSeccion}>Distribución por pregunta</h2>
                    {resultados.distribuciones.map((distribucion) => (
                        <Distribucion key={distribucion.pregunta} distribucion={distribucion} />
                    ))}

                    <h2 style={estilos.tituloSeccion}>
                        Respuestas abiertas ({resultados.respuestasAbiertas.length})
                    </h2>
                    {resultados.respuestasAbiertas.length === 0 ? (
                        <div style={estilos.vacio}>Nadie escribió comentarios en esta tanda.</div>
                    ) : (
                        resultados.respuestasAbiertas.map((abierta, indice) => (
                            <RespuestaAbierta key={indice} respuesta={abierta} />
                        ))
                    )}
                </>
            )}
        </div>
    );
};

/**
 * Promedio de una pregunta de escala.
 *
 * Deja a la vista cuántos "no aplica" quedaron afuera del cálculo: un 4,8 sobre 3 respuestas de 20
 * no dice lo mismo que un 4,8 sobre 20, y sin ese número al lado se leen igual.
 */
const TarjetaPromedio = ({ promedio }) => {
    const pregunta = buscarPregunta(promedio.pregunta);
    return (
        <div style={estilos.tarjetaPromedio}>
            <div style={estilos.preguntaPromedio}>{pregunta ? pregunta.enunciado : promedio.pregunta}</div>
            <div style={estilos.valorPromedio}>
                {promedio.promedio === null ? '—' : promedio.promedio.toFixed(2)}
                <span style={estilos.sobreCinco}>/ 5</span>
            </div>
            <div style={estilos.detallePromedio}>
                {promedio.respuestasConsideradas} {promedio.respuestasConsideradas === 1 ? 'respuesta' : 'respuestas'}
                {promedio.noAplica > 0 && (
                    <span style={estilos.noAplica}>
                        · {promedio.noAplica} sin puntuar ({pregunta?.etiquetaNoAplica || 'no aplica'})
                    </span>
                )}
            </div>
        </div>
    );
};

/** Cantidad y porcentaje de cada opción, con una barra proporcional. */
const Distribucion = ({ distribucion }) => (
    <div style={estilos.tarjeta}>
        <h3 style={estilos.preguntaDistribucion}>{enunciadoDe(distribucion.pregunta)}</h3>
        {distribucion.opciones.map((opcion) => (
            <div key={opcion.valor} style={estilos.filaOpcion}>
                <div style={estilos.etiquetaOpcion}>
                    {etiquetaDeOpcion(distribucion.pregunta, opcion.valor)}
                </div>
                <div style={estilos.barraFondo}>
                    <div
                        style={{
                            ...estilos.barra,
                            width: `${opcion.porcentaje}%`,
                            background: opcion.valor === NO_APLICA ? '#94a3b8' : COLOR_SECUNDARIO,
                        }}
                    />
                </div>
                <div style={estilos.conteo}>
                    {opcion.cantidad} <span style={estilos.porcentaje}>({opcion.porcentaje}%)</span>
                </div>
            </div>
        ))}
    </div>
);

/** Las tres respuestas de texto de una misma persona, juntas y con su fecha. */
const RespuestaAbierta = ({ respuesta }) => (
    <div style={estilos.tarjeta}>
        <div style={estilos.fecha}>
            <i className="bi bi-calendar3" style={{ marginRight: '0.4rem' }} />
            {formatearFecha(respuesta.fecha)}
        </div>
        {respuesta.loQueMasSirve && (
            <BloqueTexto titulo="Lo que más le sirve" texto={respuesta.loQueMasSirve} />
        )}
        {respuesta.queMejoraria && (
            <BloqueTexto titulo="Qué mejoraría" texto={respuesta.queMejoraria} />
        )}
        {respuesta.comentarioLibre && (
            <BloqueTexto titulo="Otros comentarios" texto={respuesta.comentarioLibre} />
        )}
    </div>
);

const BloqueTexto = ({ titulo, texto }) => (
    <div style={{ marginTop: '0.75rem' }}>
        <div style={estilos.tituloBloque}>{titulo}</div>
        <div style={estilos.textoLibre}>{texto}</div>
    </div>
);

const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const valor = new Date(fecha);
    return valor.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const estilos = {
    pagina: {
        padding: '1.5rem 1.75rem 3rem',
        maxWidth: '1000px',
        margin: '0 auto',
        minHeight: '100vh',
        background: '#f8fafc',
        fontFamily: 'var(--font-family-base)',
    },
    aviso: {
        padding: '2rem',
        textAlign: 'center',
        color: '#64748b',
        background: '#f8fafc',
        minHeight: '100vh',
    },
    encabezado: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '0.75rem',
        marginBottom: '1.25rem',
    },
    bloqueTitulo: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    },
    iconoTitulo: {
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        background: COLOR_SECUNDARIO,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.2rem',
        flexShrink: 0,
        boxShadow: 'var(--shadow-sm)',
    },
    titulo: {
        fontSize: '1.4rem',
        fontWeight: 700,
        color: '#1e293b',
        margin: 0,
    },
    subtitulo: {
        fontSize: '0.875rem',
        color: '#64748b',
        margin: '0.25rem 0 0',
    },
    acciones: {
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    selector: {
        height: '40px',
        borderRadius: '8px',
        border: '1px solid #cbd5e1',
        padding: '0 0.6rem',
        fontSize: '0.9rem',
        color: '#334155',
    },
    botonCsv: (deshabilitado) => ({
        height: '40px',
        padding: '0 1rem',
        borderRadius: '8px',
        border: 'none',
        background: deshabilitado ? '#cbd5e1' : COLOR_PRIMARIO,
        color: 'white',
        fontSize: '0.9rem',
        fontWeight: 600,
        cursor: deshabilitado ? 'default' : 'pointer',
    }),
    tarjetaTotal: {
        background: COLOR_PRIMARIO,
        color: 'white',
        borderRadius: '14px',
        padding: '1.25rem 1.5rem',
        marginBottom: '1.5rem',
    },
    numeroTotal: {
        fontSize: '2.4rem',
        fontWeight: 800,
        lineHeight: 1,
    },
    etiquetaTotal: {
        fontSize: '0.9rem',
        opacity: 0.9,
        marginTop: '0.3rem',
    },
    tituloSeccion: {
        fontSize: '1.05rem',
        fontWeight: 700,
        color: '#1e293b',
        margin: '1.75rem 0 0.75rem',
    },
    grillaPromedios: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
        gap: '0.75rem',
    },
    tarjetaPromedio: {
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '1rem',
        boxShadow: 'var(--shadow-sm)',
    },
    preguntaPromedio: {
        fontSize: '0.85rem',
        color: '#475569',
        lineHeight: 1.4,
        minHeight: '2.4em',
    },
    valorPromedio: {
        fontSize: '2rem',
        fontWeight: 800,
        color: COLOR_PRIMARIO,
        margin: '0.4rem 0 0.2rem',
    },
    sobreCinco: {
        fontSize: '0.95rem',
        fontWeight: 500,
        color: '#94a3b8',
        marginLeft: '0.3rem',
    },
    detallePromedio: {
        fontSize: '0.78rem',
        color: '#64748b',
    },
    noAplica: {
        color: '#94a3b8',
    },
    tarjeta: {
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '1rem 1.15rem',
        marginBottom: '0.75rem',
        boxShadow: 'var(--shadow-sm)',
    },
    preguntaDistribucion: {
        fontSize: '0.95rem',
        fontWeight: 600,
        color: '#1e293b',
        margin: '0 0 0.85rem',
        lineHeight: 1.4,
    },
    filaOpcion: {
        display: 'grid',
        gridTemplateColumns: 'minmax(120px, 1.2fr) 3fr minmax(80px, auto)',
        alignItems: 'center',
        gap: '0.6rem',
        marginBottom: '0.45rem',
    },
    etiquetaOpcion: {
        fontSize: '0.83rem',
        color: '#475569',
    },
    barraFondo: {
        background: '#f1f5f9',
        borderRadius: '6px',
        height: '18px',
        overflow: 'hidden',
    },
    barra: {
        height: '100%',
        borderRadius: '6px',
        transition: 'width 0.3s',
        minWidth: '2px',
    },
    conteo: {
        fontSize: '0.83rem',
        fontWeight: 600,
        color: '#334155',
        textAlign: 'right',
    },
    porcentaje: {
        fontWeight: 400,
        color: '#94a3b8',
    },
    fecha: {
        fontSize: '0.78rem',
        color: '#94a3b8',
        fontWeight: 500,
    },
    tituloBloque: {
        fontSize: '0.75rem',
        fontWeight: 700,
        color: COLOR_SECUNDARIO,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        marginBottom: '0.2rem',
    },
    textoLibre: {
        fontSize: '0.9rem',
        color: '#1e293b',
        lineHeight: 1.55,
        whiteSpace: 'pre-wrap',
    },
    vacio: {
        background: 'white',
        border: '1px dashed #cbd5e1',
        borderRadius: '12px',
        padding: '1.5rem',
        textAlign: 'center',
        color: '#64748b',
        fontSize: '0.9rem',
    },
};

export default EncuestaResultadosPage;
